import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FiArrowLeft, FiDownload, FiEye, FiList} from "react-icons/fi";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import PageHeader from "../../../../components/common/pageheader/PageHeader";
import {useAuth} from "../../../../context/AuthContext";
import {downloadExamAttemptsExcel, getExamAttempts} from "../../../../services/examAttemptsApi";
import TableEmptyState from "../../../../components/common/tableEmptyState/TableEmptyState";
import styles from "./ExamAttemptsPage.module.css";
import Loader from "../../../../components/common/Loader/Loader";
import {toStatusAz} from "../../../../utils/statusText";
import {renderAttemptStatusBadge, renderFlagBadge} from "../../../../utils/statusUtil";

const PAGE_SIZE = 10;

const ExamAttemptsPage = () => {
    const {t} = useTranslation();
    const {examId, studentNumber: paramStudentNumber} = useParams();
    const navigate = useNavigate();
    const {user} = useAuth() || {};
    const roles = user?.roles || [];
    const isStudent = roles.includes("STUDENT");
    const studentNumber = paramStudentNumber || user?.studentNumber;

    const [attempts, setAttempts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [exporting, setExporting] = useState(false);

    // The attempts endpoint is paged. This screen shows one student's attempts, so a page is usually
    // the whole set — but it still has to page, or later attempts would be silently unreachable.
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);

    const handleExport = async () => {
        if (exporting) return;
        try {
            setExporting(true);
            await downloadExamAttemptsExcel({
                examId: Number(examId),
                studentNumber,
            });
        } catch (e) {
            toast.error(t("pages.examAttempts.exportError", {defaultValue: "İxrac alınmadı"}));
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        if (!examId || !studentNumber) return;

        const fetchAttempts = async () => {
            setLoading(true);
            setError("");

            try {
                const data = await getExamAttempts({
                    examId: Number(examId),
                    studentNumber,
                    pageNo,
                    pageSize: PAGE_SIZE,
                });
                setAttempts(Array.isArray(data?.content) ? data.content : []);
                setTotalPages(data?.totalPages || 1);
                setTotalElements(data?.totalElements || 0);
            } catch (e) {
                setError(t("pages.examAttempts.loadError"));
            } finally {
                setLoading(false);
            }
        };

        fetchAttempts();
    }, [examId, studentNumber, pageNo, t]);

    const renderStatusBadge = (status) => {
        if (!status) return "-";
        const normalized = String(status).toUpperCase();
        let className = styles.statusDefault;

        if (normalized === "SUBMITTED") className = styles.statusSubmitted;
        else if (normalized === "AUTO_SUBMITTED") className = styles.statusAutoSubmitted;
        else if (normalized === "DISQUALIFIED") className = styles.statusDisqualified;
        else if (normalized === "CONFIRMED") className = styles.attemptStatusConfirmed;
        else if (normalized === "CANCELLED") className = styles.attemptStatusCancelled;

        return <span className={className}>{toStatusAz(normalized)}</span>;
    };

    return (
        <div className={styles.container}>
            <PageHeader title={t("pages.examAttempts.title")} icon={<FiList/>}/>

            <div className={styles.headerRow}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <FiArrowLeft/>
                    <span>{t("common.back")}</span>
                </button>

                {!isStudent && (
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={handleExport}
                        disabled={exporting || loading || totalElements === 0}
                        style={{marginLeft: "auto"}}
                        title={t("pages.examAttempts.exportTitle", {defaultValue: "Excel olaraq endir"})}
                    >
                        <FiDownload/>
                        <span>
                            {exporting
                                ? t("pages.examAttempts.exporting", {defaultValue: "Endirilir..."})
                                : t("pages.examAttempts.exportExcel", {defaultValue: "Excel"})}
                        </span>
                    </button>
                )}
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.tableWrapper}>
                {loading ? (
                    <Loader fullscreen/>
                ) : attempts.length === 0 ? (
                    <TableEmptyState
                        title={t("pages.examAttempts.emptyTitle")}
                        description={t("pages.examAttempts.emptyDescription")}
                    />
                ) : (
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            {!isStudent && <th>{t("pages.examAttempts.colStudent")}</th>}
                            <th>{t("common.status")}</th>
                            <th>{t("pages.examAttempts.colDecision")}</th>
                            <th>{t("pages.examAttempts.colScore")}</th>
                            <th>{t("pages.examAttempts.colCorrectTotal")}</th>
                            <th>{t("pages.examAttempts.colSuspicion")}</th>
                            {!isStudent && <th></th>}
                        </tr>
                        </thead>

                        <tbody>
                        {attempts.map((attempt) => {
                            const key = attempt.attemptId ?? attempt.id;

                            return (
                                <tr key={key} style={attempt.studentDisabled ? {opacity: 0.55} : undefined}>
                                    {!isStudent && (
                                        <td>
                                            {attempt.studentName || "-"}
                                            {attempt.studentDisabled && (
                                                <span style={{marginLeft: 8, padding: "2px 8px", borderRadius: 999, background: "#fee2e2", color: "#b91c1c", fontSize: 11, fontWeight: 600, verticalAlign: "middle"}}>
                                                    {t("pages.students.disabledBadge")}
                                                </span>
                                            )}
                                        </td>
                                    )}
                                    <td>{renderStatusBadge(attempt.status)}</td>
                                    <td>{renderAttemptStatusBadge(attempt.attemptStatus, attempt.status)}</td>
                                    <td>{attempt.score !== undefined && attempt.score !== null ? attempt.score : "-"}</td>
                                    <td>{attempt.correct !== undefined && attempt.total !== undefined ? `${attempt.correct} / ${attempt.total}` : "-"}</td>
                                    <td>{renderFlagBadge(attempt.flaggedForSuspicion)}</td>

                                    {!isStudent && (
                                        <td className={styles.actionsCell}>
                                            <button type="button" className={styles.iconButton} onClick={() =>
                                                navigate(`/exam-attempts/${key}/detail`, {
                                                    state: {
                                                        attemptStatus: attempt.attemptStatus,
                                                        myStatus: attempt.myStatus,
                                                        status: attempt.status,
                                                    },
                                                })
                                            }
                                            >
                                                <FiEye/>
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}

                {totalPages > 1 && (
                    <div className={styles.pager}>
                        <button
                            type="button"
                            onClick={() => setPageNo((p) => Math.max(p - 1, 1))}
                            disabled={loading || pageNo <= 1}
                        >
                            {t("common.previous")}
                        </button>
                        <span className={styles.pagerLabel}>
                            {t("common.pageOf", {page: pageNo, total: totalPages})}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPageNo((p) => Math.min(p + 1, totalPages))}
                            disabled={loading || pageNo >= totalPages}
                        >
                            {t("common.next2")}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamAttemptsPage;
